import { useNavigate } from 'react-router-dom';
import { CardActions, Grid, StatusChip, TaskCard } from '../ui';
import type { TaskCardAccent } from '../ui';
import type { InspectionListItem } from '../../lib/api/inspections.gen';
import { formatDistanceToNow } from '../../lib/time';
import { useVocab } from '../../lib/vocab';
import { propertyLabel } from './ItemList';
import { STATE_LABEL, STATE_TONE, STATE_WEIGHT } from '../inspections/InspectionLifecycleBar';

// ItemCards — the Inspections collection's touch-optimized alternate view
// (story 2-139, P10 §2.4's collection form rule). `ItemList` (rows) stays the
// default: an inspection's row content is genuinely operational (one
// identity, one status, one meta line, one action) per the form rule this
// story writes down in docs/prd/10-design-system.md §2. This view composes
// the EXISTING `TaskCard` primitive (story 5-2b: "one unit of work, on a
// phone") rather than inventing a second card shape — the same primitive
// My day already renders a working-queue item through, which is exactly the
// content this list carries.
//
// Distinct testids from ItemList's `item-row-*`/`item-status-*`/`item-open-*`
// (`item-card-*` below) — a deliberate choice, not an oversight: the two
// views render the SAME records at the SAME time only one of which is ever
// mounted (SegmentedControl-gated), so a shared testid would be
// ambiguous about which view a spec is asserting against.

// Story 2-146b: the status Badge below used to carry its OWN
// `STATUS_TONE` map, a near-duplicate of `ItemList.tsx`'s `statusPalette`
// (same enum, same five-ish color groupings, second copy). Removed in favour
// of `InspectionLifecycleBar`'s exported `STATE_TONE`/`STATE_LABEL`/
// `STATE_WEIGHT`, the same maps `ItemList` now reuses — one source for this
// lifecycle's tone vocabulary, not two hand-kept siblings.
//
// `STATUS_ACCENT` stays local: it feeds `TaskCard`'s own `accent` prop (a
// real design-system primitive axis, not a status-label imitation), and nine
// values collapsing onto five accents is `TaskCard`'s own contract, not a
// second `StatusChip`.
const STATUS_ACCENT: Record<InspectionListItem['status'], TaskCardAccent> = {
  draft: 'neutral',
  scheduled: 'primary',
  assigned: 'primary',
  in_progress: 'warning',
  submitted: 'primary',
  in_review: 'warning',
  approved: 'success',
  delivered: 'success',
  cancelled: 'neutral',
  completed: 'success',
};

export function ItemCards({ inspections }: { inspections: InspectionListItem[] }): JSX.Element {
  const navigate = useNavigate();
  const { vocab } = useVocab();
  const unitsLower = vocab('Units').toLowerCase();

  return (
    // Story 2-147wb: `Grid` (2-147sa), was a raw `grid gap-3 ...` div.
    <Grid columns={{ base: 1, sm: 2, lg: 3 }} gap={3} data-testid="item-cards">
      {inspections.map((inspection) => (
        <TaskCard
          key={inspection._id}
          data-testid={`item-card-${inspection._id}`}
          title={propertyLabel(inspection)}
          subtitle={`${inspection.roomsCount} ${unitsLower} captured`}
          accent={STATUS_ACCENT[inspection.status]}
          meta={
            <>
              <span data-testid={`item-card-status-${inspection._id}`}>
                <StatusChip
                  tone={STATE_TONE[inspection.status]}
                  weight={STATE_WEIGHT[inspection.status]}
                  label={STATE_LABEL[inspection.status]}
                  value={inspection.status}
                />
              </span>
              <span className="text-xs text-muted-foreground">
                Updated {formatDistanceToNow(inspection.updatedAt)}
              </span>
            </>
          }
          action={
            <CardActions
              actions={[
                {
                  id: 'open',
                  label: 'Open detail',
                  variant: 'secondary',
                  onClick: () => navigate(`/app/items/${inspection._id}`),
                  pinned: true,
                  'data-testid': `item-card-open-${inspection._id}`,
                },
              ]}
            />
          }
        />
      ))}
    </Grid>
  );
}
